import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { showToast } from "@/helpers/toast"
import { useModals } from "@/modals"
import { CURRENCY } from "@/store/apis/balanceAPI"
import { useGetUserDataQuery } from "@/store/apis/userAPI"
import type { IUser } from "@/store/apis/userAPI"
import {
  useGetStarCourseQuery,
  useGetWithdrawalConfigQuery,
  useSendVerificationMutation
} from "@/store/apis/withdrawalAPI"
import styles from "./WithdrawForm.module.scss"
import type { IWithdrawFormProps } from "./types"
import { WithdrawAmountInput, WithdrawSummary } from "./ui"

const selectTelegramWallet = ({ data }: { data?: IUser }) => ({
  walletAddress: data?.wallets?.telegram
})

export const WithdrawForm: FC<IWithdrawFormProps> = ({
  currency,
  balance,
  onCancel
}) => {
  const { t } = useTranslation()
  const { openModal } = useModals()
  const [amount, setAmount] = useState<string>("")

  const { data: config, isLoading: isConfigLoading } =
    useGetWithdrawalConfigQuery(currency)

  const {
    data: starCourse,
    isLoading: isStarCourseLoading,
    isError: isStarCourseError
  } = useGetStarCourseQuery(undefined, {
    skip: currency !== CURRENCY.STAR
  })

  const { walletAddress } = useGetUserDataQuery(undefined, {
    selectFromResult: selectTelegramWallet
  })

  const [sendVerification, { isLoading: isSending }] =
    useSendVerificationMutation()

  const handleWithdraw = async (): Promise<void> => {
    const parsedAmount = Number(amount)
    try {
      await sendVerification({ coin: currency, amount: parsedAmount }).unwrap()
      openModal("withdrawOtp", {
        coin: currency,
        amount: parsedAmount,
        onSuccess: () => {
          onCancel()
          showToast.success(t("wallet.withdraw.form.withdrawSuccess"))
        }
      })
    } catch {
      showToast.error(t("wallet.withdraw.form.verificationError"))
    }
  }

  return (
    <div className={styles.WithdrawForm}>
      <WithdrawAmountInput
        currency={currency}
        balance={balance}
        amount={amount}
        onAmountChange={setAmount}
        limitFrom={config?.limitFrom ?? 0}
        limitTo={config?.limitTo ?? 0}
        isConfigLoading={isConfigLoading}
      />
      <div className={styles.WithdrawForm_divider} />
      <WithdrawSummary
        currency={currency}
        balance={balance}
        amount={amount}
        walletAddress={walletAddress}
        commission={config?.commission ?? 0}
        withdrawPercent={config?.withdrawPercent ?? 1}
        limitFrom={config?.limitFrom ?? 0}
        limitTo={config?.limitTo ?? 0}
        isConfigLoading={isConfigLoading}
        isSubmitting={isSending}
        starCoursePrice={starCourse?.price}
        isStarCourseLoading={isStarCourseLoading}
        isStarCourseError={isStarCourseError}
        onWithdraw={handleWithdraw}
        onCancel={onCancel}
      />
    </div>
  )
}
